import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import axios from "../../axios";
import { getAccessToken } from "../../cookies";
import { useToast, FavoriteIcon, RatingPopup } from "../../components";
import { favorites, review } from "../../store";
import { Pagination } from "antd";
import BookingModal from "../../components/bookingModal/bookingModal";
import "./coachDetail.css";

const CoachDetail = () => {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();

  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redux favorites state
  const { coachMap, _pendingAdd } = useSelector((state) => state.favorites);
  const isFavorited = !!coachMap[coachId] && coachMap[coachId] !== "pending";
  const favoriteLoading = _pendingAdd[coachId] === "pending";

  const { isAuthenticated } = useSelector((state) => state.user);
  const isLoggedIn = isAuthenticated || !!getAccessToken();

  // Redux reviews state
  const {
    reviews,
    myReview,
    pagination: reviewsPagination,
    justSubmittedReview,
  } = useSelector((state) => state.review);

  // Booking modal state
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isRatingPopupOpen, setIsRatingPopupOpen] = useState(false);

  // Hardcoded dummy data (sessions handled only)
  const dummySessionsHandled = "500+";

  // Fetch favorite status on mount
  useEffect(() => {
    dispatch(favorites.fetchFavorites("COACH"));
  }, [dispatch]);

  const handleToggleFavorite = useCallback(() => {
    const favoriteDocId = coachMap[coachId];
    if (favoriteDocId && favoriteDocId !== "pending") {
      dispatch(
        favorites.removeFavorite({
          favoriteDocId,
          itemId: coachId,
          type: "COACH",
        }),
      ).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Removed from favorites");
        } else if (res.meta.requestStatus === "rejected") {
          toast.error("Failed to remove from favorites");
        }
      });
    } else {
      dispatch(favorites.addFavorite(coachId)).then((res) => {
        if (res.meta.requestStatus === "fulfilled") {
          toast.success("Added to favorites");
        } else if (res.meta.requestStatus === "rejected") {
          toast.error("Failed to add to favorites");
        }
      });
    }
  }, [dispatch, coachMap, coachId, toast]);

  useEffect(() => {
    const fetchCoachProfile = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/coaches/${coachId}/profile`);
        if (response.data.success) {
          setCoach(response.data.data);
        } else {
          setError("Failed to load coach profile");
        }
      } catch (err) {
        const errorMessage =
          err.response?.data?.message || "Failed to load coach profile";
        setError(errorMessage);
        toast.error("Error", errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (coachId) {
      fetchCoachProfile();
    }
  }, [coachId, toast]);

  const handleSlotClick = useCallback((rawSlot) => {
    setSelectedSlot(rawSlot);
    setBookingModalVisible(true);
  }, []);

  const handleBookingModalCancel = useCallback(() => {
    setBookingModalVisible(false);
    setSelectedSlot(null);
  }, []);

  const handleBookingNext = useCallback(async () => {
    const topicId = coach?.specialties?.[0]?.id;
    if (!selectedSlot?.id || !topicId) {
      toast.error("Booking Failed", "Missing slot or topic information");
      return;
    }

    try {
      const response = await axios.post("/appointments", {
        availabilityId: selectedSlot.id,
        topicId,
      });

      if (response.status === 201) {
        toast.success(
          "Booking Successful",
          `Booking successful with coach ${coach.name}`,
        );

        const profileResponse = await axios.get(`/coaches/${coachId}/profile`);
        if (profileResponse.data.success) {
          setCoach(profileResponse.data.data);
        }
      }

      setBookingModalVisible(false);
      setSelectedSlot(null);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "An error occurred while booking";
      toast.error("Booking Failed", errorMessage);
    }
  }, [coach, coachId, selectedSlot, toast]);

  // Refresh coach data when a review is just submitted
  useEffect(() => {
    if (justSubmittedReview && coach?.id) {
      const refreshCoachProfile = async () => {
        try {
          const [profileResponse] = await Promise.all([
            axios.get(`/coaches/${coachId}/profile`),
            dispatch(review.getMyCoachReview({ coachId: coach.id })),
            dispatch(
              review.getCoachReviews({
                coachId: coach.id,
                limit: reviewsPagination.pageSize,
                skip:
                  (reviewsPagination.currentPage - 1) *
                  reviewsPagination.pageSize,
              }),
            ),
          ]);

          if (profileResponse.data.success) {
            setCoach(profileResponse.data.data);
          }
        } catch (err) {
          console.error("Error refreshing coach profile:", err);
        } finally {
          dispatch(review.clearJustSubmittedReview());
        }
      };

      refreshCoachProfile();
    }
  }, [
    justSubmittedReview,
    coach?.id,
    coachId,
    dispatch,
    reviewsPagination.currentPage,
    reviewsPagination.pageSize,
  ]);

  // Fetch coach reviews via Redux
  useEffect(() => {
    if (coach?.id) {
      dispatch(
        review.getCoachReviews({ coachId: coach.id, limit: 10, skip: 0 }),
      );
    }
  }, [coach?.id, dispatch]);

  // Fetch current customer's review status for this coach
  useEffect(() => {
    const targetCoachId = coach?.id || coachId;

    if (targetCoachId && isLoggedIn) {
      dispatch(review.getMyCoachReview({ coachId: targetCoachId }));
    }

    return () => {
      dispatch(review.resetMyReview());
    };
  }, [coach?.id, coachId, isLoggedIn, dispatch]);

  const handleOpenRatingPopup = useCallback(() => {
    setIsRatingPopupOpen(true);
  }, []);

  const handleCloseRatingPopup = useCallback(() => {
    setIsRatingPopupOpen(false);
  }, []);

  const handleReviewsPageChange = (page) => {
    const skip = (page - 1) * reviewsPagination.pageSize;
    dispatch(
      review.getCoachReviews({
        coachId: coach.id,
        limit: reviewsPagination.pageSize,
        skip,
      }),
    );
  };

  // Format date for the booking modal
  const formatModalDate = (dateStr) => {
    if (!dateStr) return "";
    return dayjs(dateStr, "YYYY-MM-DD").format("ddd, MMM DD, YYYY");
  };

  // Format availability slots for display
  const formatAvailabilitySlots = (availability) => {
    if (!availability || availability.length === 0) return [];

    return availability.map((slot) => {
      const date = dayjs(slot.date, "YYYY-MM-DD");
      const today = dayjs();
      let dateLabel;

      if (date.isSame(today, "day")) {
        dateLabel = "Today";
      } else if (date.isSame(today.add(1, "day"), "day")) {
        dateLabel = "Tomorrow";
      } else {
        dateLabel = date.format("ddd, MMM DD");
      }

      // Convert 24h time to 12h format
      const formatTime = (time) => {
        if (!time) return "";
        const [h, m] = time.split(":");
        const hour = parseInt(h, 10);
        const ampm = hour >= 12 ? "PM" : "AM";
        const hour12 = hour % 12 || 12;
        return `${hour12}:${m} ${ampm}`;
      };

      return {
        date: dateLabel,
        time: `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`,
      };
    });
  };

  if (loading) {
    return (
      <div
        style={{ textAlign: "center", padding: "100px" }}
        className="coach-detail-container"
      >
        <Spin size="large" />
      </div>
    );
  }

  if (error && !coach) {
    return (
      <div
        style={{ textAlign: "center", padding: "100px" }}
        className="coach-detail-container"
      >
        <h2>Coach not found</h2>
        <p style={{ color: "#475569" }}>{error}</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            marginTop: "16px",
            padding: "8px 24px",
            background: "#08949b",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!coach) return null;

  const rawAvailabilitySlots = coach.availability || [];
  const availabilitySlots = formatAvailabilitySlots(rawAvailabilitySlots);
  const showAvailability = availabilitySlots.length > 0;

  // Map API certifications (string array) to display format
  const certifications =
    coach.certifications && coach.certifications.length > 0
      ? coach.certifications.map((cert, index) => ({
          name: typeof cert === "string" ? cert : cert.name,
          issuer: typeof cert === "string" ? "" : cert.issuer || "",
          icon: ["workspace_premium", "fitness_center", "health_and_safety"][
            index % 3
          ],
        }))
      : [];

  const showRatingsSection =
    coach.totalReviews > 0 || reviews.length > 0 || myReview.canRate;

  return (
    <div className="coach-detail-container">
      {/* Hero Profile Section */}
      <section className="hero-section">
        <div className="hero-bg" />
        <div className="hero-favorite-absolute">
          <FavoriteIcon
            isFavorited={isFavorited}
            loading={favoriteLoading}
            onToggle={handleToggleFavorite}
          />
        </div>
        <div className="hero-content">
          <img
            className="hero-profile-pic"
            src={coach.picture || "/default-avatar.png"}
            alt={coach.name}
            onError={(e) => {
              e.target.src = "/default-avatar.png";
            }}
          />
          <div className="hero-info-wrapper">
            <div className="hero-info">
              <div className="hero-name-row">
                <h2 className="hero-name">{coach.name}</h2>
              </div>
              <div className="hero-meta">
                {coach.gender && (
                  <>
                    <span className="hero-meta-item">
                      <span className="material-symbols-outlined hero-meta-icon">
                        {coach.gender === "MALE" ? "male" : "female"}
                      </span>
                      {coach.gender === "MALE"
                        ? "Male"
                        : coach.gender === "FEMALE"
                          ? "Female"
                          : coach.gender}
                    </span>
                    <span className="hero-meta-dot" />
                  </>
                )}
                <span className="hero-meta-item">
                  <span className="material-symbols-outlined hero-meta-icon">
                    location_on
                  </span>
                  {coach.location || "Location not specified"}
                </span>
              </div>
            </div>
            <div className="hero-stats">
              <p className="hero-stats-number">{dummySessionsHandled}</p>
              <p className="hero-stats-label">Sessions Handled</p>
            </div>
          </div>
        </div>
      </section>

      {/* Bento Grid Layout */}
      <div className="bento-grid">
        {/* Main Column */}
        <div className="bento-main">
          {/* Bio Card */}
          <div className="detail-card">
            <h3 className="card-title">
              <span className="material-symbols-outlined card-title-icon">
                person_book
              </span>
              Professional Bio
            </h3>
            <p className="bio-text">
              {coach.description || "No bio available."}
            </p>
            {coach.specialties && coach.specialties.length > 0 && (
              <>
                <h4 className="card-subtitle">Specialities</h4>
                <div className="specialties-list">
                  {coach.specialties.map((specialty, index) => (
                    <span key={specialty.id || index} className="specialty-tag">
                      {specialty.name || specialty}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>

          {coach.experience && (
            <div className="detail-card">
              <h3 className="card-title">
                <span className="material-symbols-outlined card-title-icon">
                  history_edu
                </span>
                Experience
              </h3>
              <div className="experience-badge">
                <div className="experience-box">
                  <p className="experience-number">{coach.experience}</p>
                  <p className="experience-label">Professional Coaching</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Side Column */}
        <div className="bento-side">
          {/* Availability Card */}
          <div className="detail-card-sm availability-card">
            <h4 className="card-title">
              <span className="material-symbols-outlined card-title-icon">
                event_available
              </span>
              Next Available
            </h4>
            {showAvailability ? (
              <div className="availability-list">
                {availabilitySlots.map((slot, index) => (
                  <div key={index} className="availability-slot">
                    <div>
                      <p className="slot-date">{slot.date}</p>
                      <p className="slot-time">{slot.time}</p>
                    </div>
                    <button
                      className="slot-add-btn"
                      aria-label="Add slot"
                      onClick={() =>
                        handleSlotClick(rawAvailabilitySlots[index])
                      }
                    >
                      <span className="material-symbols-outlined">add</span>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p
                style={{
                  color: "#475569",
                  fontSize: "14px",
                  textAlign: "center",
                  padding: "16px 0",
                }}
              >
                No upcoming availability
              </p>
            )}
          </div>

          {/* Certifications Card */}
          <div className="detail-card-sm">
            <h4 className="card-title">
              <span className="material-symbols-outlined card-title-icon">
                military_tech
              </span>
              Certifications
            </h4>
            {certifications.length > 0 ? (
              <ul className="cert-list">
                {certifications.map((cert, index) => (
                  <li key={index} className="cert-item">
                    <div className="cert-icon-box">
                      <span className="material-symbols-outlined cert-icon">
                        {cert.icon}
                      </span>
                    </div>
                    <div>
                      <p className="cert-name">{cert.name}</p>
                      {cert.issuer && (
                        <p className="cert-issuer">{cert.issuer}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p
                style={{
                  color: "#475569",
                  fontSize: "14px",
                  textAlign: "center",
                  padding: "16px 0",
                }}
              >
                No certifications listed
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      <BookingModal
        visible={bookingModalVisible}
        onCancel={handleBookingModalCancel}
        onNext={handleBookingNext}
        coachData={{
          name: coach.name,
          picture: coach.picture,
        }}
        slotData={selectedSlot}
        selectedDate={selectedSlot ? formatModalDate(selectedSlot.date) : ""}
        topicData={
          coach.specialties?.[0]?.name ||
          coach.specialties?.[0] ||
          "General Session"
        }
      />

      {/* Ratings & Reviews Section */}
      {showRatingsSection && (
        <div className="detail-card ratings-reviews-card">
          <div className="ratings-reviews-header">
            <h3 className="card-title">
              <span className="material-symbols-outlined card-title-icon">
                reviews
              </span>
              Ratings & Reviews
            </h3>
            {myReview.canRate && (
              <button
                type="button"
                className="rate-coach-btn"
                onClick={handleOpenRatingPopup}
              >
                <span className="material-symbols-outlined">star</span>
                {myReview.existingReview
                  ? "Edit your review"
                  : "Rate the coach"}
              </button>
            )}
          </div>

          {/* Average Rating */}
          <div className="ratings-summary">
            <div className="average-rating">
              <span className="rating-number">
                {coach.averageRating?.toFixed(1) || "0.0"}
              </span>
              <div className="rating-stars">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    className={`material-symbols-outlined star ${
                      star <= Math.round(coach.averageRating || 0)
                        ? "active"
                        : ""
                    }`}
                  >
                    {star <= Math.round(coach.averageRating || 0)
                      ? "star"
                      : "star_border"}
                  </span>
                ))}
              </div>
              <span className="total-reviews">
                ({coach.totalReviews}{" "}
                {coach.totalReviews === 1 ? "review" : "reviews"})
              </span>
            </div>
          </div>

          {/* Reviews List */}
          <div className="reviews-list">
            {reviews.map((reviewItem) => (
              <div key={reviewItem.id} className="review-item">
                <div className="review-header">
                  <div className="review-customer-info">
                    <div className="review-avatar">
                      <span className="material-symbols-outlined">person</span>
                    </div>
                    <div className="review-customer-details">
                      <p className="review-customer-name">
                        {reviewItem.customerFirstName}
                      </p>
                      <div className="review-rating">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            className={`material-symbols-outlined star ${
                              star <= reviewItem.rating ? "active" : ""
                            }`}
                          >
                            {star <= reviewItem.rating ? "star" : "star_border"}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {reviewItem.createdAt && (
                    <p className="review-date">
                      {dayjs(reviewItem.createdAt).format("MMM DD, YYYY")}
                    </p>
                  )}
                </div>
                {reviewItem.review && (
                  <p className="review-text">{reviewItem.review}</p>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {reviewsPagination.totalPages > 0 && (
            <div className="reviews-pagination">
              <Pagination
                simple
                current={reviewsPagination.currentPage}
                total={reviewsPagination.totalItems}
                pageSize={reviewsPagination.pageSize}
                onChange={handleReviewsPageChange}
              />
            </div>
          )}
        </div>
      )}

      <RatingPopup
        isOpen={isRatingPopupOpen}
        onClose={handleCloseRatingPopup}
        onSuccess={() => {
          dispatch(review.getMyCoachReview({ coachId: coach.id }));
        }}
        coachData={{
          id: coach.id,
          name: coach.name,
          picture: coach.picture,
        }}
        existingReview={myReview.existingReview}
      />
    </div>
  );
};

export default CoachDetail;
